import { Text as MantineText, Alert, Box, Button, Group, LoadingOverlay, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { serverTimestamp } from "firebase/firestore";
import { FormEvent, PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useConfiguracaoFormularioIdeias } from "@/modules/configuracao-formulario-ideias";
import useUserStore from "@/modules/users/store";
import { IdeiaFormProvider } from "./ideia-form-context";
import IdeiaFormFields from "./IdeiaFormFields";
import useIdeiaForm, { SimilarIdeasCallbacks } from "./useIdeiaForm";
import { publishIdeia, TIdeiaForm } from "../../firestore";
import SimilarIdeasModal from "../../modals/similar-ideas-modal";
import { getRequiredFieldsForPublish } from "../../utils/validation";

type IdeiaFormProperties = PropsWithChildren<{
  ideia: TIdeiaForm;
  readOnly?: boolean;
  onSuccess?: (ideiaId: string, variables: TIdeiaForm, context: unknown) => Promise<unknown> | undefined;
}>;

const IdeiaForm = ({ ideia, children, readOnly = false, onSuccess }: IdeiaFormProperties) => {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] = useDisclosure(false);
  const [pendingPublishData, setPendingPublishData] = useState<TIdeiaForm | null>(null);

  // Estado local para controlar o modal de ideias similares (evita problemas com context modals)
  const [similarIdeasModalOpened, setSimilarIdeasModalOpened] = useState(false);
  const [similarIdeasCallbacks, setSimilarIdeasCallbacks] = useState<SimilarIdeasCallbacks | null>(null);

  const { data: configuracao } = useConfiguracaoFormularioIdeias();
  const requiredFieldsForPublish = useMemo(() => getRequiredFieldsForPublish(configuracao), [configuracao]);

  const handleSimilarIdeasFound = useCallback((callbacks: SimilarIdeasCallbacks) => {
    setSimilarIdeasCallbacks(callbacks);
    setSimilarIdeasModalOpened(true);
  }, []);

  const handleSimilarIdeasConfirm = useCallback(() => {
    similarIdeasCallbacks?.onConfirm();
    setSimilarIdeasModalOpened(false);
    setSimilarIdeasCallbacks(null);
  }, [similarIdeasCallbacks]);

  const handleSimilarIdeasCancel = useCallback(() => {
    similarIdeasCallbacks?.onCancel();
    setSimilarIdeasModalOpened(false);
    setSimilarIdeasCallbacks(null);
  }, [similarIdeasCallbacks]);

  const { form, isPending, submit } = useIdeiaForm(ideia, {
    configuracao,
    onSimilarIdeasFound: handleSimilarIdeasFound,
    onSuccess: async (ideiaId, variables, context) => {
      try {
        if (variables.publishedAt && activeEmpresaId) {
          await publishIdeia(activeEmpresaId, ideiaId);
        }
        await onSuccess?.(ideiaId, variables, context);
      } catch (error) {
        console.error("Error in onSuccess callback:", error);
        throw error;
      }
    },
    onError: (error) => {
      console.error("Erro ao salvar ideia:", error);
      showNotification({
        title: "Erro ao salvar",
        message: "Ocorreu um erro ao salvar a ideia. Tente novamente.",
        icon: <IconAlertCircle />,
        color: "red"
      });
      // eslint-disable-next-line compat/compat
      return Promise.resolve();
    }
  });

  const genericError = useMemo(() => form.errors[""] ?? form.errors["regulamentoReference"], [form.errors]);

  const isNewIdeia = !ideia.id || !ideia.publishedAt;
  const canPublish = isNewIdeia && !readOnly;

  const validateRequiredFields = useCallback(
    (values: TIdeiaForm): boolean => {
      const missingFields = requiredFieldsForPublish.filter((field) => {
        const fieldValue = Object.prototype.hasOwnProperty.call(values, field) ? values[field] : undefined;
        const fieldValueAsString = typeof fieldValue === "string" ? fieldValue : String(fieldValue ?? "");
        return !fieldValue || fieldValueAsString.trim() === "";
      });

      if (missingFields.length > 0) {
        showNotification({
          title: "Campo obrigatório não preenchido!!",
          message: "Por favor, para Salvar a ideia, preencha todos os campos de descrição visíveis",
          icon: <IconAlertCircle />,
          color: "red"
        });
        return false;
      }
      return true;
    },
    [requiredFieldsForPublish]
  );

  const handleDraftSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      form.onSubmit((values) => {
        void submit(values);
      })(event);
    },
    [form, submit]
  );

  const handleConfirmPublish = useCallback(() => {
    if (!pendingPublishData || !activeEmpresaId) return;

    const publishedValues = {
      ...pendingPublishData,
      publishedAt: serverTimestamp(),
      status: "NOVA_IDEIA" as TIdeiaStatus,
      updatedAt: serverTimestamp()
    };

    void submit(publishedValues);
    setPendingPublishData(null);
    closeConfirmModal();
  }, [pendingPublishData, submit, closeConfirmModal, activeEmpresaId]);

  const handleCancelPublish = useCallback(() => {
    setPendingPublishData(null);
    closeConfirmModal();
  }, [closeConfirmModal]);

  const handlePublishClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault(); // Prevent form submission

      const currentValues = form.getValues();

      if (!validateRequiredFields(currentValues)) {
        return;
      }

      const transformedValues = form.getTransformedValues();

      setPendingPublishData(transformedValues);
      openConfirmModal();
    },
    [form, validateRequiredFields, openConfirmModal]
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const submitEvent = event.nativeEvent as SubmitEvent;
      const submitButton = submitEvent.submitter as HTMLButtonElement | null;
      const buttonValue = submitButton?.value;

      if (buttonValue === "RASCUNHO") {
        handleDraftSubmit(event);
      } else if (buttonValue === "SALVAR") {
        form.onSubmit((values) => {
          void submit(values);
        })(event);
      } else {
        event.preventDefault();
        console.warn("⚠️ Unexpected submission intercepted");
      }
    },
    [handleDraftSubmit, form, submit]
  );

  if (readOnly) {
    return (
      <IdeiaFormProvider form={form}>
        <Box pos="relative">
          <LoadingOverlay visible={isPending} />
          <IdeiaFormFields readOnly={readOnly} isLoading={isPending} configuracao={configuracao}>
            {children}
          </IdeiaFormFields>
        </Box>
      </IdeiaFormProvider>
    );
  }

  return (
    <>
      <IdeiaFormProvider form={form}>
        <form onSubmit={handleSubmit}>
          <Box pos="relative">
            <LoadingOverlay visible={isPending} />
            <IdeiaFormFields readOnly={readOnly} isLoading={isPending} configuracao={configuracao}>
              {children}
            </IdeiaFormFields>
          </Box>

          <Group justify={genericError ? "space-between" : "flex-end"} mt="md">
            {genericError && (
              <Alert icon={<IconAlertCircle size="1rem" />} color="red">
                {genericError}
              </Alert>
            )}

            {canPublish ? (
              <>
                <Button type="submit" value="RASCUNHO" variant="outline" loading={isPending} disabled={isPending}>
                  Salvar Rascunho
                </Button>
                <Button onClick={handlePublishClick} loading={isPending} disabled={isPending}>
                  Publicar
                </Button>
              </>
            ) : (
              <Button type="submit" value="SALVAR" loading={isPending} disabled={isPending}>
                Salvar Alterações
              </Button>
            )}
          </Group>
        </form>
      </IdeiaFormProvider>

      {/* Modal de confirmação de publicação */}
      <Modal
        opened={confirmModalOpened}
        onClose={handleCancelPublish}
        title="Deseja publicar sua ideia?"
        centered
        zIndex={1000}
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <MantineText size="sm" mb="md">
          Ao publicar sua ideia, ela será visível para todos os usuários e não poderá ser editada ou removida.
        </MantineText>

        <Group justify="flex-end">
          <Button variant="outline" onClick={handleCancelPublish}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmPublish} loading={isPending}>
            Publicar
          </Button>
        </Group>
      </Modal>

      {/* Modal local para ideias similares (evita problemas com context modals) */}
      <Modal
        opened={similarIdeasModalOpened}
        onClose={handleSimilarIdeasCancel}
        title="Atenção: Ideias Similares"
        size="lg"
        withinPortal={true}
        zIndex={1000}
        overlayProps={{ blur: 3 }}
      >
        {similarIdeasCallbacks && (
          <SimilarIdeasModal
            similarIdeas={similarIdeasCallbacks.similarIdeas}
            onConfirm={handleSimilarIdeasConfirm}
            onCancel={handleSimilarIdeasCancel}
            previewMode={false}
          />
        )}
      </Modal>
    </>
  );
};

export default IdeiaForm;
